import { zodResolver } from "@hookform/resolvers/zod"
import { ArrowLeft, Save } from "lucide-react"
import { useForm } from "react-hook-form"
import { NavLink } from "react-router"
import { z } from "zod"

import LocationSelector from "@/components/LocationSelector.tsx"
import { Button } from "@/components/ui/button.tsx"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form.tsx"
import { Input } from "@/components/ui/input.tsx"
import { Textarea } from "@/components/ui/textarea.tsx"
import UnitSelector from "@/components/UnitSelector.tsx"
import { type TagDataPoint } from "@/types/dataPointTypes.ts"

import { getFullTagPath } from "../../util/getFullTagPath.ts"
import TagBrowser from "../tagbrowser/TagBrowser.tsx"
import TagPreview from "../TagPreview.tsx"

import type { DataPointRequest } from "../../types/dataPointDTOs.ts"

const formSchema = z.object({
  name: z.string().min(1).max(255),
  locationId: z.int(),
  unitId: z.int(),
  instrument: z.string().min(1).max(255),
  description: z.string(),
  path: z.object({
    tag: z.string().min(1),
    historyProvider: z.string().min(1),
    driver: z.string().min(1),
  }),
})

export default function TagDataPointForm({
  dataPoint,
  onSubmit,
  mutating,
}: {
  dataPoint?: TagDataPoint
  onSubmit: (dataPoint: DataPointRequest) => void
  mutating: boolean
}) {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: dataPoint && {
      name: dataPoint.name,
      locationId: dataPoint.locationId,
      unitId: dataPoint.unitId,
      instrument: dataPoint.instrument,
      description: dataPoint.description,
      path: {
        tag: dataPoint.path?.tag ?? undefined,
        historyProvider: dataPoint.path?.historyProvider ?? undefined,
        driver: dataPoint.path?.driver ?? undefined,
      },
    },
  })
  const handleSubmit = (data: z.infer<typeof formSchema>) => {
    const req: DataPointRequest = {
      $type: "tag",
      description: data.description || "",
      name: data.name,
      locationId: data.locationId,
      unitId: data.unitId,
      path: data.path,
      instrument: data.instrument,
    }
    onSubmit(req)
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-md shadow-sm border p-4">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="grid gap-4 grid-cols-2">
            <div className="flex flex-col gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Data point name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="locationId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location</FormLabel>
                    <LocationSelector {...field} />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="unitId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Unit</FormLabel>
                    <UnitSelector {...field} />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="instrument"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Source Instrument</FormLabel>
                    <FormControl>
                      <Input placeholder="Data point source instrument" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        className="h-[200px]"
                        placeholder="Data point description"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div>
              <FormField
                control={form.control}
                name="path"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tag Path</FormLabel>
                    <FormControl>
                      <TagBrowser {...field} />
                    </FormControl>
                    <FormDescription>
                      Selected: <b>{getFullTagPath(field.value || {})}</b>
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex justify-end gap-2 col-span-2">
              <NavLink to="/datapoints/defs">
                <Button variant="outline" type="button" disabled={mutating}>
                  <ArrowLeft />
                  Cancel
                </Button>
              </NavLink>
              <Button variant="default" type="submit" disabled={mutating}>
                <Save />
                Save
              </Button>
            </div>
          </form>
        </Form>
      </div>
      <div className="rounded-md shadow-sm border">
        <TagPreview path={form.watch().path || {}} />
      </div>
    </div>
  )
}
